import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

type DeletedTable = 'posts' | 'campaigns' | 'organizations' | 'short_links';

// Generic deleted record type with common fields
interface DeletedRecord {
  id: string;
  organization_id: string;
  deleted_at: string | null;
  [key: string]: unknown;
}

// UUID validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function validateUUID(id: string, fieldName: string): void {
  if (!UUID_REGEX.test(id)) {
    throw new ServiceError(`Invalid ${fieldName} format`, 'VALIDATION_ERROR');
  }
}

function validateTable(table: string): asserts table is DeletedTable {
  const validTables: DeletedTable[] = ['posts', 'campaigns', 'organizations', 'short_links'];
  if (!validTables.includes(table as DeletedTable)) {
    throw new ServiceError(`Invalid table name: ${table}`, 'VALIDATION_ERROR');
  }
}

export class AdminService {
  private static readonly logger = logger.child({ service: 'AdminService' });

  /**
   * List soft-deleted records (super admin only)
   * Note: Dynamic table name requires type assertion - safe because table is validated
   */
  static async getDeleted(
    table: DeletedTable,
    organizationId: string
  ): Promise<DeletedRecord[]> {
    try {
      validateTable(table);
      validateUUID(organizationId, 'organizationId');
      
      this.logger.info('Fetching deleted records', { table, organizationId });

      // Type assertion required: Supabase doesn't support dynamic table names in type system
      // Safe because: (1) table is validated against DeletedTable union, (2) all these tables
      // have id, organization_id, and deleted_at columns
      const { data, error } = await supabase
        .from(table as 'posts')  // Use concrete table for type inference, validated table ensures safety
        .select('*')
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;

      this.logger.info('Deleted records fetched', {
        table,
        organizationId,
        count: data?.length || 0,
      });

      return (data || []) as DeletedRecord[];
    } catch (error) {
      this.logger.error('Failed to fetch deleted records', error, { table, organizationId });
      throw error;
    }
  }

  /**
   * Restore a soft-deleted record (super admin only)
   */
  static async restore(table: DeletedTable, id: string): Promise<void> {
    try {
      validateTable(table);
      validateUUID(id, 'record id');
      
      this.logger.info('Restoring deleted record', { table, id });

      const { error } = await supabase
        .from(table)
        .update({ deleted_at: null })
        .eq('id', id);

      if (error) throw error;

      this.logger.info('Record restored successfully', { table, id });
    } catch (error) {
      this.logger.error('Failed to restore record', error, { table, id });
      throw error;
    }
  }

  /**
   * Permanently delete a record (super admin only)
   * WARNING: This action cannot be undone
   */
  static async permanentlyDelete(table: DeletedTable, id: string): Promise<void> {
    try {
      validateTable(table);
      validateUUID(id, 'record id');
      
      this.logger.warn('PERMANENTLY deleting record', { table, id });

      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id);

      if (error) throw error;

      this.logger.warn('Record permanently deleted', { table, id });
    } catch (error) {
      this.logger.error('Failed to permanently delete record', error, { table, id });
      throw error;
    }
  }

  /**
   * List all deleted records across all tables for an organization
   */
  static async getAllDeleted(organizationId: string) {
    return withServiceErrorHandling(async () => {
      validateUUID(organizationId, 'organizationId');
      
      this.logger.info('Fetching all deleted records', { organizationId });

      const tables: DeletedTable[] = [
        'posts',
        'campaigns',
        'organizations',
        'short_links',
      ];

      const results = await Promise.all(
        tables.map(async (table) => {
          try {
            const records = await this.getDeleted(table, organizationId);
            return { table, records };
          } catch (error) {
            this.logger.error('Failed to fetch deleted records', error, { 
              table, 
              organizationId 
            });
            return { table, records: [], error };
          }
        })
      );

      const totalCount = results.reduce(
        (sum, result) => sum + result.records.length,
        0
      );

      this.logger.info('All deleted records fetched', {
        organizationId,
        totalCount,
      });

      return results;
    }, { operation: 'getAllDeleted' });
  }
}
